import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiEdit2 } from 'react-icons/fi';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import ToggleButton from '../../layout/ReusableComponent/Buttons/ToggleButton';
import TextEditor from '../../layout/ReusableComponent/Text-editor/TextEditor';
import { commentBodyIsEffectivelyEmpty } from '../../layout/ReusableComponent/Text-editor/textEditorBodyUtils';
import { useAppShellHeaderOffset } from '../../layout/ReusableComponent/audioPlayer/useAppShellHeaderOffset';
import { normalizeStorageBoxNewFileName, storageBoxFileFromEditorPayload } from '../utils/storageBoxNewTextFile';
import { serializeEditorPayloadForSave } from '../utils/storageBoxTextFileEditor';
import {
  readStorageBoxTextEditorAutosaveEnabled,
  writeStorageBoxTextEditorAutosaveEnabled,
  STORAGE_BOX_TEXT_EDITOR_AUTOSAVE_DEBOUNCE_MS,
} from '../utils/storageBoxTextEditorAutosave';
import StorageBoxTextEditorLocation, {
  StorageBoxTextEditorFileIcon,
  TextEditorSaveLocation,
} from './StorageBoxTextEditorLocation';

const EDITOR_VISIBLE = {
  flag: false,
  cancel: false,
};

export default function StorageBoxNewTextFileModal({
  isOpen,
  kind,
  editorKey,
  busy,
  onClose,
  onSave,
  /** When set, shown instead of Storage Box folder path (standalone product editor). */
  saveLocationPath = '',
  fileMenuUserLoggedIn = true,
}) {
  const isRich = kind === 'rich';
  const defaultName = isRich ? 'Untitled document.html' : 'Untitled.txt';

  const [fileName, setFileName] = useState(defaultName);
  const [displayName, setDisplayName] = useState(defaultName);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = useState(() => readStorageBoxTextEditorAutosaveEnabled());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contentEpoch, setContentEpoch] = useState(0);

  const payloadRef = useRef({
    html: '',
    markdown: '',
    mode: isRich ? 'rich' : 'markdown',
    documentPageSettings: null,
  });
  const renameInputRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const autosaveEnabledRef = useRef(autosaveEnabled);
  const savingRef = useRef(false);

  useAppShellHeaderOffset(isOpen);

  useEffect(() => {
    autosaveEnabledRef.current = autosaveEnabled;
  }, [autosaveEnabled]);

  useEffect(
    () => () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    setFileName(defaultName);
    setDisplayName(defaultName);
    setRenaming(false);
    setRenameDraft('');
    setRenameBusy(false);
    setDirty(false);
    payloadRef.current = {
      html: '',
      markdown: '',
      mode: isRich ? 'rich' : 'markdown',
      documentPageSettings: null,
    };
  }, [isOpen, defaultName, isRich, editorKey]);

  useEffect(() => {
    if (!renaming) return undefined;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
    return undefined;
  }, [renaming]);

  const editorKind = isRich ? 'rich' : 'plain';
  const isBusy = busy || saving;

  const syncDirty = useCallback(
    (payload) => {
      const text = serializeEditorPayloadForSave(payload, editorKind);
      const isDirty = !commentBodyIsEffectivelyEmpty(text);
      setDirty(isDirty);
      return isDirty;
    },
    [editorKind]
  );

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  const persistNewFile = useCallback(
    async ({ notifyOnSuccess = true, saveAs = false } = {}) => {
      if (busy || savingRef.current) return false;
      const text = serializeEditorPayloadForSave(payloadRef.current, editorKind);
      if (commentBodyIsEffectivelyEmpty(text)) {
        if (notifyOnSuccess) {
          /* no toast for empty autosave */
        }
        return false;
      }
      savingRef.current = true;
      setSaving(true);
      try {
        const normalizedName = normalizeStorageBoxNewFileName(fileName, kind);
        const file = storageBoxFileFromEditorPayload(payloadRef.current, kind, normalizedName);
        await onSave(file, normalizedName, { saveAs });
        return true;
      } catch {
        return false;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [busy, editorKind, fileName, kind, onSave]
  );

  const scheduleAutosave = useCallback(() => {
    if (!autosaveEnabledRef.current || busy) return;
    clearAutosaveTimer();
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void persistNewFile({ notifyOnSuccess: false });
    }, STORAGE_BOX_TEXT_EDITOR_AUTOSAVE_DEBOUNCE_MS);
  }, [busy, clearAutosaveTimer, persistNewFile]);

  const handleAutosaveToggle = useCallback(
    (next) => {
      setAutosaveEnabled(next);
      writeStorageBoxTextEditorAutosaveEnabled(next);
      if (!next) {
        clearAutosaveTimer();
      } else if (dirty) {
        scheduleAutosave();
      }
    },
    [clearAutosaveTimer, dirty, scheduleAutosave]
  );

  const onChange = useCallback(
    (payload) => {
      payloadRef.current = payload;
      const isDirty = syncDirty(payload);
      if (isDirty && autosaveEnabledRef.current) {
        scheduleAutosave();
      }
    },
    [syncDirty, scheduleAutosave]
  );

  const handleSave = useCallback(async () => {
    clearAutosaveTimer();
    await persistNewFile({ notifyOnSuccess: true, saveAs: false });
  }, [clearAutosaveTimer, persistNewFile]);

  const handleSaveAsStorageBox = useCallback(async () => {
    clearAutosaveTimer();
    await persistNewFile({ notifyOnSuccess: true, saveAs: true });
  }, [clearAutosaveTimer, persistNewFile]);

  const handleSaveAsLocalBrowser = useCallback(async () => {
    if (fileMenuUserLoggedIn) return;
    clearAutosaveTimer();
    await persistNewFile({ notifyOnSuccess: false, saveAs: true });
  }, [clearAutosaveTimer, fileMenuUserLoggedIn, persistNewFile]);

  const startRename = useCallback(() => {
    if (renameBusy || busy || saving) return;
    setRenameDraft(displayName || fileName);
    setRenaming(true);
  }, [renameBusy, busy, saving, displayName, fileName]);

  const cancelRename = useCallback(() => {
    if (renameBusy) return;
    setRenaming(false);
    setRenameDraft('');
  }, [renameBusy]);

  const submitRename = useCallback(() => {
    if (!renaming || renameBusy) return;
    const trimmed = renameDraft.trim();
    if (!trimmed) return;
    const normalized = normalizeStorageBoxNewFileName(trimmed, kind);
    setFileName(normalized);
    setDisplayName(normalized);
    setRenaming(false);
    setRenameDraft('');
  }, [renaming, renameBusy, renameDraft, kind]);

  const handleRenameKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelRename();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        submitRename();
      }
    },
    [cancelRename, submitRename]
  );

  const handleFileMenuNew = useCallback(() => {
    if (isBusy) return;
    clearAutosaveTimer();
    setFileName(defaultName);
    setDisplayName(defaultName);
    setRenaming(false);
    setRenameDraft('');
    setDirty(false);
    payloadRef.current = {
      html: '',
      markdown: '',
      mode: isRich ? 'rich' : 'markdown',
      documentPageSettings: null,
    };
    setContentEpoch((epoch) => epoch + 1);
  }, [isBusy, clearAutosaveTimer, defaultName, isRich]);

  const handleFileMenuDocumentLoaded = useCallback(
    (parsed) => {
      const nextName = normalizeStorageBoxNewFileName(parsed?.title || defaultName, kind);
      setFileName(nextName);
      setDisplayName(nextName);
      setDirty(true);
    },
    [defaultName, kind]
  );

  const handleRequestClose = useCallback(() => {
    if (busy || saving) return;
    if (renaming) {
      cancelRename();
      return;
    }
    clearAutosaveTimer();
    if (autosaveEnabled && dirty) {
      void persistNewFile({ notifyOnSuccess: false }).finally(() => {
        onClose();
      });
      return;
    }
    onClose();
  }, [
    busy,
    saving,
    renaming,
    cancelRename,
    clearAutosaveTimer,
    autosaveEnabled,
    dirty,
    persistNewFile,
    onClose,
  ]);

  const overlayClassName = 'sb-text-editor-modal-overlay--sheet';
  const contentClassName = 'sb-text-editor-modal sb-text-editor-modal--sheet';

  const contentStyle = useMemo(
    () => ({
      width: '100%',
      maxWidth: '100%',
      height: 'calc(100dvh - var(--app-audio-sheet-top, 0px))',
      maxHeight: 'calc(100dvh - var(--app-audio-sheet-top, 0px))',
      minHeight: 0,
      margin: 0,
      borderRadius: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }),
    []
  );

  const title = displayName || fileName;

  return (
    <CenterModalWindow
      isOpen={isOpen}
      onClose={handleRequestClose}
      width="100%"
      height="100%"
      overlayClassName={overlayClassName}
      contentClassName={contentClassName}
      contentStyle={contentStyle}
      closeButtonTooltip="Close editor"
    >
      <div className="sb-text-editor-modal__body" data-modal-scroll-port>
        <header className="sb-text-editor-modal__head">
          <div className="sb-text-editor-modal__head-bar">
            <div className="sb-text-editor-modal__title-block">
              {saveLocationPath ? (
                <TextEditorSaveLocation path={saveLocationPath} />
              ) : (
                <StorageBoxTextEditorLocation />
              )}
              <div className="sb-text-editor-modal__title-row">
              {renaming ? (
                <>
                  <StorageBoxTextEditorFileIcon
                    name={fileName}
                    kind={isRich ? 'rich' : 'plain'}
                  />
                  <input
                    ref={renameInputRef}
                    type="text"
                    className="sb-text-editor-modal__title-input sb-preview__title-input"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={handleRenameKeyDown}
                    maxLength={240}
                    disabled={renameBusy || isBusy}
                    aria-label="File name"
                  />
                  <div className="sb-text-editor-modal__rename-actions">
                    <button
                      type="button"
                      className="sb-text-editor-modal__rename-apply"
                      onClick={submitRename}
                      disabled={renameBusy || isBusy || !renameDraft.trim()}
                      title="Apply new file name"
                      aria-label="Apply new file name"
                    >
                      <FiCheck aria-hidden />
                      <span>Apply name</span>
                    </button>
                    <button
                      type="button"
                      className="sb-text-editor-modal__rename-cancel"
                      onClick={cancelRename}
                      disabled={renameBusy || isBusy}
                      title="Discard name change"
                      aria-label="Discard name change"
                    >
                      Cancel edit
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="sb-text-editor-modal__filename">
                    <StorageBoxTextEditorFileIcon
                    name={fileName}
                    kind={isRich ? 'rich' : 'plain'}
                  />
                    <h2 className="sb-text-editor-modal__title" title={title}>
                      {title}
                    </h2>
                  </div>
                  <div className="sb-preview__head-actions">
                    <button
                      type="button"
                      className="sb-preview__head-action"
                      onClick={startRename}
                      disabled={isBusy}
                      title="Rename file"
                      aria-label="Rename file"
                    >
                      <FiEdit2 aria-hidden />
                    </button>
                  </div>
                </>
              )}
              </div>
            </div>
            <div
              className="sb-text-editor-modal__autosave-row"
              role="group"
              aria-label={`Autosave, ${autosaveEnabled ? 'on' : 'off'}`}
            >
              <span className="sb-text-editor-modal__autosave-label">Autosave</span>
              <ToggleButton
                checked={autosaveEnabled}
                onToggle={handleAutosaveToggle}
                disabled={isBusy}
                className="sb-text-editor-modal__autosave-toggle"
              />
              <span className="sb-text-editor-modal__autosave-status" aria-live="polite">
                {isBusy
                  ? 'Saving…'
                  : autosaveEnabled
                    ? dirty
                      ? 'Unsaved'
                      : 'Ready'
                    : dirty
                      ? 'Manual'
                      : null}
              </span>
            </div>
          </div>
        </header>
        <div className="sb-text-editor-modal__editor">
          <TextEditor
            key={`${editorKey}-${contentEpoch}`}
            className="sb-text-editor-modal__text-editor"
            lockEditorMode
            documentExport
            exportBaseFileName={title}
            defaultMode={isRich ? 'rich' : 'markdown'}
            defaultToolbarVisible
            showContentFlags={false}
            visible={EDITOR_VISIBLE}
            richPlaceholder={isRich ? '' : 'Write plain text or markdown…'}
            markdownPlaceholder="Write plain text or markdown…"
            onChange={onChange}
            fileMenuUserLoggedIn={fileMenuUserLoggedIn}
            fileMenuAutosaveEnabled={autosaveEnabled}
            onFileMenuSave={handleSave}
            onFileMenuSaveAsStorageBox={fileMenuUserLoggedIn ? handleSaveAsStorageBox : undefined}
            onFileMenuSaveAsLocal={!fileMenuUserLoggedIn ? handleSaveAsLocalBrowser : undefined}
            onFileMenuNew={handleFileMenuNew}
            onFileMenuExit={handleRequestClose}
            onFileMenuDocumentLoaded={handleFileMenuDocumentLoaded}
            primaryActionText={!autosaveEnabled ? (isBusy ? 'Saving…' : 'Save') : ''}
            onPrimaryAction={!autosaveEnabled ? handleSave : undefined}
            primaryActionDisabled={isBusy || !dirty}
            primaryActionsOnRight
            submitOnModifierEnter={!autosaveEnabled}
          />
        </div>
      </div>
    </CenterModalWindow>
  );
}
